/**
 * Transcript helpers shared by the transcription runner, MCP and the UI (docs/SPEC.md §10).
 */

export interface Segment {
  start: number;
  end: number;
  text: string;
}

/** The spoken hook: text of segments that START within the first `seconds` (default 5). */
export function hookFromSegments(segments: Segment[], seconds = 5): { text: string | null; endS: number | null } {
  const hook = segments.filter((s) => s.start < seconds && s.text.trim());
  if (!hook.length) return { text: null, endS: null };
  return { text: hook.map((s) => s.text.trim()).join(' '), endS: hook[hook.length - 1]!.end };
}

export const wordCount = (s: string) => (s.trim() ? s.trim().split(/\s+/u).length : 0);

/**
 * Transliteration must not add or drop words: each output segment keeps the input's word count
 * within ±tolerance (default 15%, at least ±1 word for short segments).
 */
export function sameLength(original: string, converted: string, tolerance = 0.15): boolean {
  const a = wordCount(original);
  const b = wordCount(converted);
  if (a === 0) return b === 0;
  return Math.abs(a - b) <= Math.max(1, Math.round(a * tolerance));
}

/** Prompt version is stored on every transcript so policy changes can be re-run selectively. */
export const SCRIPT_POLICY_VERSION = 'sp-2026-10-14';

/** The language/script rule every LLM prompt that touches transcripts must include (owner rule, SPEC §10.3). */
export const SAME_SCRIPT_RULE =
  'Reply in the same language and script as the source text. Do not translate. Hinglish stays in Roman (Latin) script. ' +
  'Hindi stays in Devanagari. Never output Urdu (Arabic/Nastaliq) script for Hindi or Hinglish speech.';

/**
 * Whisper `prompt`s we used to send (removed by decision 79, 2026-10-21). Whisper writes a sentence-like prompt out as
 * if it were speech when it hears none (a music intro): reel CqcKGd0MBAl's transcript and hook began with the Hinglish
 * one. No prompt is sent any more (the Hindi pass forces `language=hi`; Roman script comes from the chat model). Kept
 * only to find and clean transcripts saved while they were sent (Admin → Storage re-check, stored-text re-checks).
 */
export const PAST_WHISPER_PROMPTS: readonly string[] = [
  'Haan bhai, aaj main aapko ek simple trick bataunga jo sach mein kaam karti hai.',
  'नमस्ते दोस्तों, आज मैं आपको एक आसान तरीका बताऊँगा।',
  // The Devanagari one as the transliteration writes it in Roman (Hinglish display text).
  'Namaste doston, aaj main aapko ek aasan tarika bataunga.',
];

const WORD = /[\p{L}\p{M}\p{N}']+/gu;
/** Words of a text, lower-cased, punctuation and symbols dropped (Indic vowel signs kept). */
const leakWords = (t: string) => (t.toLowerCase().match(WORD) ?? []).map((w) => w.replace(/'/g, ''));

/**
 * Share of a prompt's words that must appear in a segment, in order and next to each other (a misheard word allowed), for
 * it to be a leak (decision 79). A loose word overlap (60%) was tried first and removed in review: it dropped real
 * openers such as "Namaste doston, aaj main aapko ek bahut important cheez bataunga".
 */
export const PROMPT_LEAK_MIN_SHARE = 0.8;
/** Fewer words than this in a row is never a leak. */
export const PROMPT_LEAK_MIN_WORDS = 6;

/** Whisper mishears words of the prompt ("Haan bhai" → "bai"): one edit apart (two for words of 6+ letters) still counts. */
function nearWord(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const max = Math.max(a.length, b.length) >= 6 ? 2 : 1;
  if (Math.abs(a.length - b.length) > max || Math.min(a.length, b.length) < 3) return false;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[b.length]! <= max;
}

/**
 * Longest run of consecutive segment words matching consecutive prompt words (nearWord): [start in the segment, length,
 * start in the prompt].
 */
function longestSharedRun(seg: string[], prompt: string[]): [number, number, number] {
  let best: [number, number, number] = [0, 0, 0];
  const row = new Array<number>(prompt.length + 1).fill(0);
  for (let i = 1; i <= seg.length; i++) {
    let diag = 0;
    for (let j = 1; j <= prompt.length; j++) {
      const up = row[j]!;
      row[j] = nearWord(seg[i - 1], prompt[j - 1]) ? diag + 1 : 0;
      if (row[j]! > best[1]) best = [i - row[j]!, row[j]!, j - row[j]!];
      diag = up;
    }
  }
  return best;
}

/**
 * A segment's text with any Whisper prompt it repeats cut out (decision 79). A leak is a run of at least 80% of a
 * prompt's words, in order and next to each other (at least 6; a misheard word counts, e.g. "bai, aaj main aapko ek
 * simple trick bataunga jo sach mein kaam karti hai." for "Haan bhai, …"). Returns:
 *   - 'drop': nothing but the prompt (and punctuation) was in the segment;
 *   - a string: the segment without the prompt (speech around it kept);
 *   - null: no leak.
 * A creator really saying "aaj main aapko ek simple trick bataunga" (7 of the prompt's 15 words) is kept.
 */
export function stripPromptLeak(text: string, prompts: readonly string[]): 'drop' | string | null {
  let out = text;
  let changed = false;
  for (const prompt of prompts) {
    const p = leakWords(prompt);
    const s = leakWords(out);
    if (!p.length || !s.length) continue;
    const [at, len] = longestSharedRun(s, p);
    if (len < Math.max(PROMPT_LEAK_MIN_WORDS, Math.ceil(p.length * PROMPT_LEAK_MIN_SHARE))) continue;
    // Cut the run out of the original text: its first word to its last word, plus the punctuation and spaces after it.
    const words = [...out.matchAll(WORD)];
    const from = words[at]!.index!;
    const last = words[at + len - 1]!;
    let to = last.index! + last[0].length;
    while (to < out.length && /[\s\p{P}\p{S}]/u.test(out[to]!)) to++;
    out = (out.slice(0, from) + out.slice(to)).replace(/\s+/g, ' ').trim();
    changed = true;
  }
  if (!changed) return null;
  return leakWords(out).length ? out : 'drop';
}


/** Whether a text repeats one of these Whisper prompts (by default the ones we used to send). */
export const isPromptLeak = (text: string, prompts: readonly string[] = PAST_WHISPER_PROMPTS) => stripPromptLeak(text, prompts) !== null;

/**
 * Segments without Whisper prompt leaks (decision 79): a leaked segment is dropped, a prompt inside a longer segment is
 * cut out. `leaked` counts the segments dropped or changed. Runs before the hook is computed.
 */
export function dropPromptLeaks<S extends Segment>(segments: S[], prompts: readonly string[]): { segments: S[]; leaked: number } {
  if (!prompts.length) return { segments, leaked: 0 };
  let leaked = 0;
  const out: S[] = [];
  for (const s of segments) {
    const r = stripPromptLeak(s.text, prompts);
    if (r === null) out.push(s);
    else {
      leaked++;
      if (r !== 'drop') out.push({ ...s, text: r });
    }
  }
  return { segments: leaked ? out : segments, leaked };
}

export const monthStartUtc = (t: number) => {
  const d = new Date(t);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
};

/** Shown while an imported transcript waits for the script policy check (policy_status ≠ 'ok'): no hook yet. */
export const HOOK_PENDING_TEXT = 'The spoken hook appears after the language check, which runs daily. Imported transcripts can take a few days.';

/**
 * What the spoken-hook box says when there is no hook (app reel panel and extension side panel). A transcript that is
 * shown but waiting for the language check must not be told to "transcribe" (there's nothing to transcribe).
 */
export function missingHookText(t: { pending?: boolean } | null | undefined): string {
  if (t?.pending) return HOOK_PENDING_TEXT;
  if (t) return 'No spoken hook in the first seconds of this reel.';
  return 'No spoken hook yet. Transcribe the reel to get it.';
}
